interface SelectOption {
  value: string | number;
  label: string;
}

interface Props {
  id: string;
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export default function FormSelect({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  options,
  placeholder,
  required,
  disabled,
}: Props) {
  const errorId = `${id}-error`;
  const hasError = Boolean(error);

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink-primary">
        {label}
        {required && <span className="text-brand-warm-deep ml-1" aria-hidden="true">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        className={`
          w-full px-3 py-2 bg-surface-app-elevated border rounded-lg text-ink-primary
          focus:outline-none focus:ring-2 transition-colors
          ${error
            ? 'border-brand-warm focus:ring-red-500/50'
            : 'border-border-strong focus:ring-brand-primary/50 focus:border-brand-primary'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${!value ? 'text-ink-muted' : ''}
        `}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hasError && (
        <p id={errorId} className="text-sm text-brand-warm-deep" role="alert">{error}</p>
      )}
    </div>
  );
}
